// React Imports
import { ReactElement, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Third-Party Library Imports
import Table, { ColumnsType } from "antd/es/table";
import { Pagination, TableColumnsType, Tooltip } from "antd";
import { useSelector } from "react-redux";
import clsx from "clsx";
import $ from "jquery";
import { CaretUpFilled, CaretDownFilled } from "@ant-design/icons";

// Service Imports
import moment from "moment";
import CustomerService from "../customer.service";
import User from "src/models/user";
import CustomSearch from "src/components/custom-search/custom-search";
import CustomModal from "src/components/custom-modal/custom-modal";
import DeleteModal from "src/components/delete-modal/delete-modal";
import EditCustomerForm from "../edit-customer-form/edit-customer-form";
import { BUSINESS_ACCOUNT_BRANCH } from "src/utils/data/constant";

const expandColumns: TableColumnsType = [
  {
    title: "Account",
    dataIndex: "accountCode",
    key: "accountCode",
    sorter: (a: any, b: any) => a?.accountCode - b?.accountCode,
    render: (_, record: any) =>
      record.accountCode && record.branch !== "SA" ? (
        <Link
          to={`/home/business-account/${record.id}/${record.activeKycId}/details`}
          className="underline text-blue-500"
        >
          {`Account#${record.accountCode}`}
        </Link>
      ) : (
        <Link
          to={
            record?.branch === "SA"
              ? `/home/new-kyc/${record.id}/${record.pendingKycId || record.activeKycId}`
              : `/home/business-account/${record.id}/${record.pendingKycId}/details`
          }
          className="underline  text-[#FFAF00]"
        >
          {record?.accountCode ? `Account#${record.accountCode}` : `New account`}
        </Link>
      ),
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    sorter: (a: any, b: any) =>
      String(a?.clientSubType?.description).localeCompare(
        String(b?.clientSubType?.description)
      ),
    render: (_, record: any) => (
      <span>{record?.clientSubType?.description}</span>
    ),
  },
  {
    title: "Branch",
    dataIndex: "branch",
    key: "branch",
    sorter: (a: any, b: any) =>
      String(a?.branch).localeCompare(String(b?.branch)),
    render: (_, record: any) => (
      <span>{BUSINESS_ACCOUNT_BRANCH[record?.branch]}</span>
    ),
  },
  {
    title: "Wealth manager",
    dataIndex: "wealthManger",
    key: "wealthManger",
    sorter: (a: any, b: any) =>
      String(a?.wealthManager?.ae1).localeCompare(
        String(b?.wealthManager?.ae1)
      ),
    render: (_, record: any) => (
      <span>
        {record?.wealthManager?.ae1 ? record?.wealthManager?.ae1 : "-"}
      </span>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: (a: any, b: any) =>
      String(a?.accountStatus?.description).localeCompare(
        String(b?.accountStatus?.description)
      ),
    render: (_, record: any) => (
      <div className="flex items-center gap-1">
        <span
          className={clsx(
            "w-2 h-2 rounded-full ",
            record?.accountStatus?.description
              ?.toLowerCase()
              ?.includes("pending")
              ? "bg-[#FFAF00]"
              : record?.accountStatus?.description
                ?.toLowerCase()
                ?.includes("blocked") ||
                record?.accountStatus?.description
                  ?.toLowerCase()
                  ?.includes("closed")
                ? "bg-red-200"
                : "bg-[#00B050]"
          )}
        ></span>
        <span>{record?.accountStatus?.description}</span>
      </div>
    ),
  },
  {
    title: "Approval status",
    dataIndex: "approvalStatus",
    key: "approvalStatus",
    render: (_, record: any) => {
      const length = record?.kycReview?.length;
      if (record?.kycReview[length - 1]?.groupCstatus === "Pending") {
        return (
          <div className="flex items-center gap-1">
            <span className={clsx("w-2 h-2 rounded-full bg-[#FFAF00]")}></span>
            <span>Group C</span>
          </div>
        );
      } else if (record?.kycReview[length - 1]?.groupBstatus === "Pending") {
        return (
          <div className="flex items-center gap-1">
            <span className={clsx("w-2 h-2 rounded-full bg-[#FFAF00]")}></span>
            <span>Group B</span>
          </div>
        );
      } else if (record?.kycReview[length - 1]?.groupAstatus === "Pending") {
        return (
          <div className="flex items-center gap-1">
            <span className={clsx("w-2 h-2 rounded-full bg-[#FFAF00]")}></span>
            <span>Group A</span>
          </div>
        );
      } else if (
        record?.accountStatus?.description === "Pending" &&
        record?.kycReview?.length === 0
      ) {
        return <span>-</span>;
      } else {
        if (
          record?.accountStatus?.description !== "Pending" &&
          (record?.pendingKycId || record?.pendingDocumentsId)
        ) {
          return <span>-</span>;
        } else {
          return (
            <div className="flex items-center gap-1">
              <span
                className={clsx("w-2 h-2 rounded-full bg-[#00B050]")}
              ></span>
              <span>Approved</span>
            </div>
          );
        }
      }
    },
  },
];

const VerifiedList: React.FC = (): ReactElement => {
  let searchTimer = null;
  const customerService = new CustomerService();
  const loadingState = useSelector((state: any) => state?.loading);
  const user = useSelector((state: any) => state.user);
  const [users, setUsers] = useState<User[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState<User>();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [openSendResetLink, setOpenSendResetLink] = useState<boolean>(false);
  const [openForceUpdatePassword, setOpenForceUpdatePassword] =
    useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>();

  useEffect(() => {
    fetchCustomersListData();
  }, []);

  const sendResetLink = async () => {
    const response = await customerService?.sendResetPasswordEmail(
      selectedCustomer?.id,
      selectedCustomer?.email
    );
    if (response) {
      setOpenSendResetLink(false);
    }
  };

  const forceUpdatePassword = async () => {
    const response = await customerService?.forceUpdatePassword(
      selectedCustomer?.id
    );
    if (response) {
      setOpenForceUpdatePassword(false);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: "Full name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
      fixed: "left",
      render: (_, record: User) =>
        record?.account?.length &&
          user?.permissions?.includes("view_business_accounts") ? (
          <Link to={`/home/customer-profile/${record.id}`}>
            {record.firstName} {record.lastName}
          </Link>
        ) : (
          <span>
            {record.firstName} {record.lastName}
          </span>
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
    },
    {
      title: "Mobile number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
      render: (_, record: User) => (
        <div className="flex items-center">
          {/* <img src={flags[record.code]} alt={text} /> */}
          <span>{record.phoneNumber}</span>
        </div>
      ),
    },
    {
      title: "Accounts",
      dataIndex: "account",
      key: "account",
      width: 140,
      render: (_, record: User) =>
        record?.account?.length ? (
          <span
            className=" cursor-pointer underline text-blue-500 flex items-center gap-2"
            onClick={() => {
              if (expandedRowKeys.includes(record?.id)) {
                setExpandedRowKeys([]);
                setSelectedRecord(null);
                return;
              } else {
                setExpandedRowKeys([record?.id]);
                setSelectedRecord(record);
              }
            }}
          >
            {expandedRowKeys.includes(record?.id) ? (
              <img src="/images/minus-square.svg" alt="add" title="minus" />
            ) : (
              <img src="/images/add-square.svg" alt="add" title="add" />
            )}
            <span>{record?.account?.length} Accounts</span>
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
      render: (text: string) => (
        <span
          className={clsx(
            "rounded bg-gray-600 text-sm font-medium w-[80px] flex items-center justify-center h-[35px]",
            text !== "pending"
              ? "bg-gray-600 text-green-500"
              : "bg-pink-200 text-pink-300"
          )}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Locked",
      dataIndex: "lockoutUntil",
      key: "lockoutUntil",
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
      render: (text) => (
        <span className="flex justify-center text-gray-500">
          {text ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Last login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 190,
      sorter: true,
      sortIcon: (order: any) => {
        return order?.sortOrder === "ascend" ? (
          <div className="flex h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
          </div>
        ) : order?.sortOrder === "descend" ? (
          <div className="flex flex-col gap-0 h-[30px]">
            <CaretUpFilled style={{ color: "#002F6C" }} />
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        ) : (
          <div className="flex h-[30px]">
            <CaretDownFilled style={{ color: "#002F6C" }} />
          </div>
        );
      },
      render: (text) => (
        <span className="flex justify-center text-gray-500">
          {text ? moment(text).format("DD MMM, YYYY hh:mm A") : "-"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 110,
      render: (_, record: User) => (
        <div className="flex items-center gap-[10px] w-16">
          {user?.permissions?.includes("edit_customers") && (
            <Tooltip title="Edit Customer">
              <img
                src="/images/edit.svg"
                onClick={() => {
                  setSelectedUser(record);
                  setOpenEditModal(true);
                }}
              />
            </Tooltip>
          )}
          {user?.permissions?.includes("access_password_mgnt") && (
            <Tooltip title="Send Reset Link">
              <img
                src="/images/reset-link-icon.svg"
                onClick={() => {
                  setSelectedCustomer(record);
                  setOpenSendResetLink(true);
                }}
              />
            </Tooltip>
          )}
          {user?.permissions?.includes("access_password_mgnt") && (
            <Tooltip title="Force update password">
              <img
                src="/images/force-update-icon.svg"
                onClick={() => {
                  setSelectedCustomer(record);
                  setOpenForceUpdatePassword(true);
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const fetchCustomersListData = async (page = 1): Promise<void> => {
    const data = await customerService.getCustomersListData(
      page,
      searchWord,
      sortField,
      sortOrder,
      '["verified"]'
    );
    if (data !== undefined) {
      setResponseDate(data);
      const sortData = data.users.sort((a, b) =>
        b.status.localeCompare(a.status)
      );
      setUsers(sortData);
    }
  };

  useEffect(() => {
    fetchCustomersListData();
  }, [searchWord, sortField, sortOrder]);

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSelectedRecord(null);
      setExpandedRowKeys([]);
      setSearchWord(e.target.value);
    }, 1000);
  };

  const expandedRowRender = () => (
    <Table
      columns={expandColumns}
      dataSource={selectedRecord?.account}
      pagination={false}
    />
  );

  return (
    <div className="flex flex-col customers-table">
      <div className="mt-5 border-t-[1px] border-gray-400 rounded-lg">
        <div className=" flex items-center justify-between rounded-t-lg bg-white py-[10px] px-5">
          <p className=" text-black-300 text-base font-bold">
            {!loadingState ? users?.length : ""} Customers
          </p>
          <div className="flex items-center">
            <CustomSearch onChange={(event) => onSearch(event)} />
          </div>
        </div>
        {users?.length ? (
          <>
            <Table
              columns={columns}
              expandable={{ expandedRowRender, defaultExpandedRowKeys: ["0"] }}
              onChange={handleTableChange}
              pagination={false}
              dataSource={users}
              expandIconColumnIndex={8}
              rowKey={(record) => record.id}
              expandedRowKeys={expandedRowKeys}
              onExpand={(expand, record: any) => {
                setTimeout(() => {
                  $(".ant-table-expanded-row").each(function () {
                    const display = $(this).css("display");
                    if (display == "none") {
                      $(this).prev().removeClass("active");
                    } else {
                      $(this).prev().addClass("active");
                    }
                  });
                }, 0);
              }}
              expandIcon={({ expanded, onExpand, record }) => null}
            />
            <div className="table-container">
              <Pagination
                total={responseDate?.totalCount}
                defaultCurrent={1}
                pageSize={responseDate?.pageSize}
                // current={responseDate?.currentPage - 1}
                showTotal={(total, range) => {
                  const [start, end] = range;
                  return `Showing ${start} to ${end} of ${total} entries`;
                }}
                showSizeChanger={false}
                onChange={(page) => {
                  fetchCustomersListData(page);
                }}
              />
            </div>
          </>
        ) : !loadingState ? (
          <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
            <img
              className="w-[40px] h-[40px]"
              src="/images/no-items.svg"
              alt="no data kamco"
            />
            <p className="text-lg text-gray-500 font-medium">
              There are no items in this list
            </p>
          </div>
        ) : (
          ""
        )}
      </div>

      <CustomModal
        isVisible={openDeleteModal}
        centered
        width={450}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          onCancel={() => setOpenDeleteModal(false)}
          text="Delete business customer!"
          subText="Are you sure to delete this customer?"
        />
      </CustomModal>

      <CustomModal
        isVisible={openEditModal}
        centered
        width={900}
        showClose={false}
        onClose={() => {
          setOpenEditModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <EditCustomerForm
            selectedUser={selectedUser}
            onSuccess={() => {
              fetchCustomersListData();
              setOpenEditModal(false);
            }}
            onCancel={() => {
              setOpenEditModal(false);
            }}
          />
        </div>
      </CustomModal>

      <CustomModal
        isVisible={openSendResetLink}
        centered
        width={450}
        onClose={() => {
          setOpenSendResetLink(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          icon="/images/confidential-email.svg"
          onCancel={() => setOpenSendResetLink(false)}
          onConfirm={sendResetLink}
          text="Reset password link!"
          subText={`Are you sure you want to send reset password link?`}
        />
      </CustomModal>

      <CustomModal
        isVisible={openForceUpdatePassword}
        centered
        width={450}
        onClose={() => {
          setOpenForceUpdatePassword(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          icon="/images/reset-password.svg"
          onCancel={() => setOpenForceUpdatePassword(false)}
          onConfirm={forceUpdatePassword}
          text="Force update password!"
          subText={`Are you sure you want to force update password?`}
        />
      </CustomModal>
    </div>
  );
};

export default VerifiedList;
